import axios, { AxiosError, AxiosInstance, isAxiosError } from "axios";
import { matchPath } from "react-router-dom";
import { authStore } from "@/store/authStore";
import { getBasePath, getRootApiUrl } from "@/lib/apiConfig";
import {
    SessionEndReason,
    emitSessionStarting,
    emitTokenEstablished,
    postSessionEnd,
    subscribeSessionEnd,
} from "@/lib/sessionChannel";
import { getIdleMs, resetIdleClock, setSessionIdleLimitSec } from "@/lib/idleClock";
import { redirectToLogin } from "@/store/authUtils";
import { useAuthToastStore } from "@/store/authToastStore";

/**
 * The single FE auth module (CT-36). Owns the pbx-api /api/auth/* transport:
 * - login/refresh/logout on a bare axios instance (no shared interceptors, so a refresh call
 *   can never recurse through the 401-retry handler below)
 * - the in-memory access token lifecycle: silent boot refresh, proactive rotation at
 *   expires_in - 120s, and the coalesced 401 refresh-and-retry shared by BOTH API clients
 *
 * The access token itself lives in authStore.authToken (memory only — never persisted).
 * The refresh credential is the __Host-pbx_rt HttpOnly cookie on the pbx-api origin; the FE
 * never sees it, it only rides via withCredentials.
 */

export type AuthMfaChallenge = {
    channels: string[];
    selected: string;
    sent: boolean;
};

export class AuthApiError extends Error {
    status: number;
    code: string;
    mfa?: AuthMfaChallenge;
    /** rest-kit's invalid_body detail — [{ path, message }] naming the rule that was broken. */
    details?: { path: string; message: string }[];
    /** Seconds to wait, on a too_many_requests. The server also writes the wait into `message`, but
     *  that sentence is English — this is the number, so the screen can say it in the user's own. */
    retryAfter?: number;
    /** CT-371: the address a login_location_denied refusal names, so the message can show it. */
    ip?: string;
    /** CT-376: with password_expired — opens the "choose a new password" page. */
    resetToken?: string;

    constructor(
        status: number,
        code: string,
        mfa?: AuthMfaChallenge,
        details?: { path: string; message: string }[],
        retryAfter?: number
    ) {
        super(code);
        this.name = "AuthApiError";
        this.status = status;
        this.code = code;
        this.mfa = mfa;
        this.details = details;
        this.retryAfter = retryAfter;
    }
}

type AuthTokenEnvelope = {
    ok: boolean;
    data?: {
        access_token: string;
        expires_in: number | null;
        token_type: string;
        /** CT-318: the workspace's own origin once routed to this FE (login only). */
        app_origin?: string | null;
        /** CT-372: the tenant's UI inactivity limit. */
        idle_limit_sec?: number | null;
    };
    error?: string;
    mfa?: AuthMfaChallenge;
    details?: { path: string; message: string }[];
    /** seconds, on a 429 */
    retry_after?: number;
    /** CT-371: where a login_location_denied sign-in came from */
    ip?: string | null;
    /** CT-376: with password_expired */
    reset_token?: string | null;
};

/** Exported for tests only — production code must go through the functions below. */
export const authAxios: AxiosInstance = axios.create({
    withCredentials: true,
    headers: { Accept: "application/json" },
});
// Resolved per request (not at module init) so the module never touches env/config during
// import — keeps the import graph side-effect free for consumers and tests.
authAxios.interceptors.request.use((config) => {
    config.baseURL = getRootApiUrl();
    return config;
});

const PROACTIVE_REFRESH_LEEWAY_SEC = 120;
const MIN_PROACTIVE_DELAY_SEC = 30;
const FAILED_REFRESH_RETRY_SEC = 30;

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshInFlight: Promise<string | null> | null = null;
let bootPromise: Promise<void> | null = null;
/**
 * Wall-clock deadline of the in-memory token. setTimeout alone cannot be trusted for it: a
 * suspended laptop freezes timers, so on resume the timer is overdue by however long the
 * machine slept and the token is already dead. The watchdog compares against this instead.
 */
let tokenExpiresAtMs: number | null = null;
/** Idempotency latch for endSession() — parallel 401s must produce ONE logout, not a storm. */
let sessionEnded = false;
/**
 * Why the last definitive refresh deny happened, when the server named a reason worth showing
 * on the login page. performRefresh is the only code that sees the deny slug, but endSession is
 * called by its CALLERS (rotation tick, 401 interceptor), which only see null — this hands the
 * reason across without changing their contract. Consumed (cleared) by endSession.
 */
let refreshDenyReason: SessionEndReason | null = null;
/**
 * Where the document was standing when the session state was dropped.
 *
 * endSession() skips its reload when the user is already on the login page — but the moment the
 * token goes, DashboardLayoutWrapper's gate client-side-redirects to /login on its own. Any path
 * that clears the session a task BEFORE calling endSession (the idle cut awaits
 * logoutSession first) therefore found /login in window.location and skipped the reload that IS
 * the teardown: the webphone, its socket and the query cache kept running on top of the login
 * form (CT-36 QA round 4). Read the path from when the session died, not from after the gate
 * moved us.
 */
let pathAtSessionClear: string | null = null;

function clearProactiveRefreshTimer(): void {
    if (proactiveRefreshTimer !== null) {
        clearTimeout(proactiveRefreshTimer);
        proactiveRefreshTimer = null;
    }
}

function scheduleProactiveRefresh(expiresInSec: number | null): void {
    clearProactiveRefreshTimer();
    if (!expiresInSec || expiresInSec <= 0) {
        return;
    }
    const delaySec = Math.max(expiresInSec - PROACTIVE_REFRESH_LEEWAY_SEC, MIN_PROACTIVE_DELAY_SEC);
    proactiveRefreshTimer = setTimeout(() => {
        void runProactiveRefresh();
    }, delaySec * 1000);
}

/**
 * The rotation tick. A transient failure must NOT disarm rotation — dropping the timer on the
 * first blip leaves the tab holding a token it will never renew, which is how a slept laptop
 * came back with an expired token and a /v1 scope probe that answered "no access".
 */
async function runProactiveRefresh(): Promise<void> {
    try {
        const freshToken = await refreshSession();
        if (!freshToken) {
            // Definitive deny on the tick — the session is over and nobody else will notice.
            // Without this the tab keeps its painted dashboard holding a null token, and that
            // null then disarms the `!!authToken` guard in the 401 handler below, so the user's
            // next click can never reach endSession() either. That is CT-36 AC3.
            endSession();
        }
    } catch {
        // Transient (network/5xx). refreshSession() already left the session intact; try again
        // shortly rather than waiting for a user-triggered 401 to notice.
        clearProactiveRefreshTimer();
        proactiveRefreshTimer = setTimeout(() => {
            void runProactiveRefresh();
        }, FAILED_REFRESH_RETRY_SEC * 1000);
    }
}

/** Put a fresh access token into the store (memory only) and schedule its rotation. */
export function setSessionToken(accessToken: string, expiresInSec: number | null, idleLimitSec?: number | null): void {
    // Establishing a session (login, hosted callback, boot) restarts the idle window; ROTATING one
    // must not. Resetting on every rotation would be the old /prolong-session bug rebuilt — a
    // background tick that silently proves the user is present when nobody is there.
    if (!authStore.getState().authToken) {
        resetIdleClock();
        // Before the token flips: the shell mounts on it and must not read the last user's cache (QA #7).
        emitSessionStarting();
    }

    authStore.setState((state) => {
        state.authToken = accessToken;
        state.isLoggedIn = true;
        state.isUnauthorized = false;
    });
    tokenExpiresAtMs = expiresInSec && expiresInSec > 0 ? Date.now() + expiresInSec * 1000 : null;
    if (idleLimitSec !== undefined) {
        setSessionIdleLimitSec(idleLimitSec);
    }
    // A live token means a live session — re-arm the logout latch so the NEXT death still fires
    // (endSession can return without reloading the document when it lands on /login).
    sessionEnded = false;
    pathAtSessionClear = null;
    // And drop any deny reason left over from a PREVIOUS session (boot idle-deny never reaches
    // endSession, so the flag survives a fresh SPA login and would mislabel a later reason-less
    // end — e.g. a manual logout in another tab — as "inactivity").
    refreshDenyReason = null;
    scheduleProactiveRefresh(expiresInSec);
    // Caches that answered an authority question against the PREVIOUS token (/api/config, which
    // carries the scopes) must re-ask now — otherwise an answer taken while the token was expired
    // sticks for its whole staleTime and the migrated modules stay missing from the sidebar.
    emitTokenEstablished();
}

/** Drop the in-memory session (does not touch the HttpOnly refresh cookie). */
export function clearSessionState(): void {
    // First clear of this session wins: the auth gate reacts to it by navigating, so anything
    // read after this point describes where the gate went, not where the session died.
    pathAtSessionClear ??= window.location.pathname;
    clearProactiveRefreshTimer();
    tokenExpiresAtMs = null;
    setSessionIdleLimitSec(null);
    authStore.setState((state) => {
        state.authToken = null;
        state.isLoggedIn = false;
    });
}

/**
 * The single "this session is over, get the user out" path (CT-36 QA round 1).
 *
 * Before this existed, clearSessionState() had no subscriber: the only thing that actually
 * logged the app out was the legacy isUnauthorized flag, which apiConfig deliberately
 * suppresses for every silentError request — i.e. every migrated /v1 screen. A revoked refresh
 * cookie therefore left the dashboard sitting on "Something went wrong" with a Retry button
 * that could never recover (the credential needed to recover is gone).
 *
 * Deliberately a hard redirect, not a router navigate: it tears down sockets, the webphone and
 * the whole query cache for free, and the boot refresh on the way back in re-derives the truth.
 */
export function endSession(options: { broadcast?: boolean; reason?: SessionEndReason } = {}): void {
    const { broadcast = true } = options;
    if (sessionEnded) {
        return;
    }
    // logout() owns its own teardown + redirect; racing it would double-navigate mid-cleanup.
    if (authStore.getState().isLoggingOut) {
        return;
    }
    sessionEnded = true;

    // Pin this BEFORE clearSessionState(): see pathAtSessionClear.
    const pathWhenSessionDied = pathAtSessionClear ?? window.location.pathname;

    // The reason travels per-tab (broadcast payload + /login?reason=…), NEVER via the shared
    // persisted toast store alone: N reloading login pages racing to consume one localStorage
    // toast is the QA-3 "banner in one tab only" bug.
    const reason = options.reason ?? refreshDenyReason ?? undefined;
    refreshDenyReason = null;

    if (broadcast) {
        postSessionEnd(reason);
    }
    clearSessionState();

    // Already on the login screen (e.g. a background poll died just as the user landed there):
    // clearing state is enough — reloading would only throw away a half-typed form. The mounted
    // login page reacts to the store, so the banner still shows without a navigation.
    if (pathWhenSessionDied.endsWith("/login")) {
        if (reason) {
            useAuthToastStore.getState().addToast(reason);
        }
        return;
    }
    redirectToLogin(reason);
}

/**
 * Another tab reported the session ended. The refresh cookie is already gone server-side, so
 * there is nothing to call — just stop being logged in here too.
 */
function handleRemoteSessionEnd(reason?: SessionEndReason): void {
    if (authStore.getState().isLoggingOut) {
        return;
    }
    if (!authStore.getState().authToken) {
        // This tab never had a live session (sitting on /login, or already ended). Drop any
        // residue without reloading it.
        clearSessionState();
        return;
    }
    endSession({ broadcast: false, reason });
}

/**
 * Re-arm rotation when the tab comes back from a suspend/offline stretch. `visibilitychange`
 * and `online` are the two moments where wall-clock time may have jumped far past what the
 * pending setTimeout believes, so re-check the real deadline and refresh early if we are inside
 * the leeway (or already past it).
 */
function handleSessionWakeup(): void {
    if (document.visibilityState !== "visible") {
        return;
    }
    const { authToken } = authStore.getState();
    if (!authToken || tokenExpiresAtMs === null) {
        return;
    }
    if (Date.now() >= tokenExpiresAtMs - PROACTIVE_REFRESH_LEEWAY_SEC * 1000) {
        void runProactiveRefresh();
    }
}

/**
 * Wire the session to the browser: cross-tab logout + the suspend/resume watchdog.
 * Called once from main.tsx alongside bootAuthSession().
 */
export function initSessionSync(): () => void {
    const unsubscribeSessionEnd = subscribeSessionEnd(handleRemoteSessionEnd);
    document.addEventListener("visibilitychange", handleSessionWakeup);
    window.addEventListener("online", handleSessionWakeup);

    return () => {
        unsubscribeSessionEnd();
        document.removeEventListener("visibilitychange", handleSessionWakeup);
        window.removeEventListener("online", handleSessionWakeup);
    };
}

function toAuthApiError(error: unknown): AuthApiError {
    if (isAxiosError(error) && error.response) {
        const body = (error.response.data ?? {}) as AuthTokenEnvelope;
        const apiError = new AuthApiError(
            error.response.status,
            body.error || "auth_failed",
            body.mfa,
            body.details,
            typeof body.retry_after === "number" ? body.retry_after : undefined
        );
        if (body.ip) apiError.ip = body.ip;
        if (body.reset_token) apiError.resetToken = body.reset_token;
        return apiError;
    }
    return new AuthApiError(0, "network_error");
}

/**
 * Silent refresh — single-flight: every concurrent caller shares one POST /auth/refresh.
 * Resolves the new access token, or null when the server definitively denied the refresh
 * (missing/invalid/revoked-family cookie → the caller must treat the session as dead).
 * Throws on transient failures (network, 5xx like rbac_unavailable — the BE deliberately
 * keeps the cookie there, so we must NOT log the fleet out on a blip).
 */
export function refreshSession(): Promise<string | null> {
    if (!refreshInFlight) {
        refreshInFlight = performRefresh().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
}

/** Webphone SDK token provider (CT-302 8.4): rotates near expiry or on forceRefresh; null = signed out. */
export async function getAccessToken(options: { forceRefresh?: boolean } = {}): Promise<string | null> {
    const current = authStore.getState().authToken;
    if (!current) {
        return current;
    }
    const nearExpiry = tokenExpiresAtMs !== null && tokenExpiresAtMs - Date.now() < PROACTIVE_REFRESH_LEEWAY_SEC * 1000;
    if (!options.forceRefresh && !nearExpiry) {
        return current;
    }
    try {
        return await refreshSession();
    } catch (error) {
        if (options.forceRefresh) {
            throw error;
        }
        return current;
    }
}

async function performRefresh(): Promise<string | null> {
    // Guard against wiping/overwriting a session established while this refresh was in
    // flight (the boot refresh races the public impersonate page and a fast login submit):
    // the result only applies if nobody else wrote a token meanwhile.
    const tokenAtStart = authStore.getState().authToken;
    const sessionUnchanged = () => authStore.getState().authToken === tokenAtStart;

    try {
        // The idle age rides with the refresh so the server can refuse an idle session.
        // It is an assertion by this browser, not proof — sip-idp also enforces its own
        // server-observed refresh cadence for the case where nobody is running our JS at all.
        const response = await authAxios.post<AuthTokenEnvelope>("/auth/refresh", {
            idle_ms: getIdleMs(),
        });
        const data = response.data?.data;
        if (!data?.access_token) {
            return null;
        }
        if (!sessionUnchanged()) {
            return authStore.getState().authToken;
        }
        setSessionToken(data.access_token, data.expires_in, data.idle_limit_sec);
        return data.access_token;
    } catch (error) {
        // Definitive deny is EXACTLY 401 (no/invalid/revoked cookie per the pbx-api
        // contract). Anything else — 403 origin_not_allowed from a CORS misstep, an edge
        // 429, 5xx rbac_unavailable — is transient and must never log the fleet out.
        if (isAxiosError(error) && error.response?.status === 401) {
            if (sessionUnchanged()) {
                // The server refused because the session went idle — say so on the login
                // page instead of the generic expiry copy. Covers the case where the server noticed
                // first, e.g. a tab that was suspended past the cut. The toast handles the
                // no-reload path (boot refresh → router lands on /login); the deny reason is
                // picked up by endSession for the reload + cross-tab paths.
                const denial = (error.response.data as AuthTokenEnvelope | undefined)?.error;
                // CT-376: the password the session was opened with expired; the next login meets the
                // choose-a-new-password step, so tell them that rather than "your session expired".
                refreshDenyReason =
                    denial === "idle_timeout"
                        ? "inactivity"
                        : denial === "password_expired"
                        ? "password_expired"
                        : denial === "account_closed"
                        ? "account_closed"
                        : null;
                if (refreshDenyReason) {
                    useAuthToastStore.getState().addToast(refreshDenyReason);
                }
                clearSessionState();
                return null;
            }
            return authStore.getState().authToken;
        }
        throw error;
    }
}

/**
 * Boot-time silent refresh. Started once per page load (from main.tsx, before React renders)
 * so the network round-trip overlaps app bootstrapping; RootRedirect and
 * DashboardLayoutWrapper hold their paint on authStore.bootStatus. Skipped on the public TV page.
 */
export function bootAuthSession(): Promise<void> {
    if (!bootPromise) {
        bootPromise = performBoot();
    }
    return bootPromise;
}

/**
 * The TV page (CT-55) authenticates by its board hash. A session there would clear the query cache
 * mid-load (the board's first read) and arm the inactivity logout on an unattended screen.
 * Matched exactly as the router matches the route: case-insensitive, any trailing slashes.
 */
function isPublicTvPage(pathname: string): boolean {
    const basePath = getBasePath().replace(/\/+$/, "");
    return matchPath(`${basePath}/wallboards/:hash`, pathname) !== null;
}

async function performBoot(): Promise<void> {
    try {
        if (!isPublicTvPage(window.location.pathname)) {
            await refreshSession();
        }
    } catch {
        // Transient boot failure → land unauthenticated; login remains available.
    } finally {
        authStore.setState((state) => {
            state.bootStatus = "ready";
        });
    }
}

/** Test-only: reset module singletons between cases. */
export function resetAuthSessionForTests(): void {
    clearProactiveRefreshTimer();
    refreshInFlight = null;
    bootPromise = null;
    tokenExpiresAtMs = null;
    sessionEnded = false;
    refreshDenyReason = null;
    pathAtSessionClear = null;
}

export type LoginCredentials = {
    email: string;
    password: string;
    otp?: string;
    /** CT-129: ask for the code over this channel instead of the one sip-idp picks first. */
    channel?: string;
};

/** CT-129 — finish an SSO login the tenant's 2FA policy paused. The browser carries nothing but
 *  the HttpOnly pending cookie pbx-api set at the callback; the identity and scopes never leave
 *  the server. Answers where to land once the code checks out — no access token here, the session
 *  is the refresh cookie and the app picks it up on boot, exactly as a normal SSO callback does.
 *
 *  Passing only `channel` (no code) re-sends over that channel instead of verifying. */
export async function completeSsoMfa(input: { otp?: string; channel?: string }): Promise<string> {
    try {
        const response = await authAxios.post<{ ok: boolean; landing?: string }>("/auth/sso/mfa", input);
        return response.data?.landing || "/";
    } catch (error) {
        throw toAuthApiError(error);
    }
}

/** CT-563 — whose account the "Enter your password to connect" step is for. The verified
 *  provider identity stays server-side; only its email and provider come back. */
export async function readSsoLink(): Promise<{ email: string; provider: string }> {
    try {
        const response = await authAxios.get<{ ok: boolean; data: { email: string; provider: string } }>(
            "/auth/sso/link"
        );
        return response.data.data;
    } catch (error) {
        throw toAuthApiError(error);
    }
}

/** CT-563 — connect the provider with the account password. Resolves the landing origin once
 *  signed in (pbx-api set the refresh cookie). A tenant that requires 2FA rejects with
 *  mfa_required and its challenge; the SSO code step (?sso_mfa) finishes it. */
export async function completeSsoLink(password: string): Promise<string> {
    try {
        const response = await authAxios.post<{ ok: boolean; landing?: string }>("/auth/sso/link", { password });
        return response.data?.landing || "/";
    } catch (error) {
        throw toAuthApiError(error);
    }
}

/**
 * POST /api/auth/login. On success the token is placed in memory and the rotating
 * __Host-pbx_rt cookie is set by the server. Failures throw AuthApiError with the platform
 * error slug (invalid_credentials, mfa_required (+ mfa challenge), invalid_code, mfa_locked,
 * no_access, ...).
 */
export async function loginWithCredentials(credentials: LoginCredentials): Promise<{ appOrigin: string | null }> {
    try {
        const response = await authAxios.post<AuthTokenEnvelope>("/auth/login", credentials);
        const data = response.data?.data;
        if (!data?.access_token) {
            throw new AuthApiError(response.status, "auth_failed");
        }
        setSessionToken(data.access_token, data.expires_in, data.idle_limit_sec);
        return { appOrigin: data.app_origin ?? null };
    } catch (error) {
        if (error instanceof AuthApiError) {
            throw error;
        }
        throw toAuthApiError(error);
    }
}

/** What happened AFTER the password was changed. `signedIn: false` is not a failure — the change
 *  landed, the session just did not follow (a tenant that mandates 2FA or SSO, or a service blip). */
export type PasswordResetOutcome = { signedIn: true } | { signedIn: false; reason: string; mfa?: AuthMfaChallenge };

type PasswordResetEnvelope = {
    ok: boolean;
    data?: {
        signed_in: boolean;
        reason?: string;
        mfa?: AuthMfaChallenge;
        access_token?: string;
        expires_in?: number | null;
        token_type?: string;
        idle_limit_sec?: number | null;
    };
    error?: string;
};

/**
 * POST /api/auth/forgot-password.
 *
 * Resolves whatever the address is. The endpoint answers identically for an account, no account, a
 * malformed address and a throttled one, deliberately — so there is nothing here to branch on, and
 * a caller that tried would be inventing a distinction the server refuses to make.
 */
export async function requestPasswordReset(email: string): Promise<void> {
    try {
        await authAxios.post("/auth/forgot-password", { email }, { skipAuthRefresh: true });
    } catch (error) {
        throw toAuthApiError(error);
    }
}

/**
 * POST /api/auth/reset-password. Throws only when the password did NOT change — a spent or expired
 * link (invalid_or_expired_token), a password the rules refuse (invalid_password), or the identity
 * service being unreachable. Anything else resolves, because by then the change is committed and
 * the link is gone: reporting it as an error would send the person back to a dead link.
 */
export async function redeemPasswordReset(token: string, password: string): Promise<PasswordResetOutcome> {
    try {
        // skipAuthRefresh: a 401 here means the LINK is spent, not that a session expired. Without
        // it, a signed-in tab opening a stale reset link runs the refresh-and-retry interceptor —
        // logging the working session out, or replaying the POST with an already-burned token.
        const response = await authAxios.post<PasswordResetEnvelope>(
            "/auth/reset-password",
            { token, password },
            { skipAuthRefresh: true }
        );
        const data = response.data?.data;
        if (!data) {
            throw new AuthApiError(response.status, "auth_failed");
        }
        if (data.signed_in && data.access_token) {
            setSessionToken(data.access_token, data.expires_in ?? null, data.idle_limit_sec);
            return { signedIn: true };
        }
        return { signedIn: false, reason: data.reason ?? "unavailable", mfa: data.mfa };
    } catch (error) {
        if (error instanceof AuthApiError) {
            throw error;
        }
        throw toAuthApiError(error);
    }
}

export type EmailChangeOutcome = { status: "confirmed" | "already_confirmed"; email: string };

// authAxios has no timeout; a hung refresh must not hold the confirm screen.
const DROP_CHECK_TIMEOUT_MS = 5000;

/** Confirming revokes the changed identity's sessions; an unrelated session (e.g. the admin's) refreshes fine. */
async function dropSessionIfRevoked(): Promise<void> {
    try {
        // A boot or rotation refresh already in flight may predate the revoke, so let it land and ask again.
        await bootPromise;
        try {
            await refreshInFlight;
        } catch {
            // Its failure says nothing about the revoke; the fresh refresh below decides.
        }
        if (!authStore.getState().authToken) {
            return;
        }
        if ((await refreshSession()) === null) {
            // Not endSession(): its redirect would hide the success message on this page.
            // A 200 without a token also lands here and does not clear on its own.
            clearSessionState();
            const reason = refreshDenyReason ?? undefined;
            refreshDenyReason = null;
            postSessionEnd(reason);
        }
    } catch {
        // Transient: the next rotation tick notices the revoked family.
    }
}

function dropSessionIfRevokedWithin(ms: number): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms);
    });
    return Promise.race([dropSessionIfRevoked(), timeout]).finally(() => clearTimeout(timer));
}

/**
 * CT-419: POST /api/auth/verify-email. Refusals are 400/409/429 (never 401), and skipAuthRefresh
 * keeps a signed-in tab's session out of them. After a confirm, a tab whose session the change
 * revoked is signed out (and tells the other tabs).
 */
export async function confirmEmailChange(token: string): Promise<EmailChangeOutcome> {
    try {
        const response = await authAxios.post("/auth/verify-email", { token }, { skipAuthRefresh: true });
        const payload = response.data?.data ?? response.data;
        if (payload?.status !== "confirmed" && payload?.status !== "already_confirmed") {
            throw new AuthApiError(response.status, "unexpected_response");
        }
        await dropSessionIfRevokedWithin(DROP_CHECK_TIMEOUT_MS);
        return { status: payload.status, email: String(payload.email ?? "") };
    } catch (error) {
        if (error instanceof AuthApiError) {
            throw error;
        }
        throw toAuthApiError(error);
    }
}

/**
 * POST /api/auth/logout (clears the refresh cookie server-side) + drop local session.
 *
 * Also tells the other tabs. Their access tokens are in memory and still verify offline for up
 * to their remaining TTL, so without this signal they keep working against a revoked family
 * until they happen to refresh — QA saw exactly that (tab B stayed usable until reloaded).
 */
export async function logoutSession(reason?: SessionEndReason): Promise<void> {
    try {
        await authAxios.post("/auth/logout");
    } catch {
        // Best effort — local cleanup below is what logs the tab out; the rt cookie
        // otherwise dies by TTL.
    } finally {
        clearSessionState();
        // This signal reaches the other tabs FIRST. The cut path's endSession() does broadcast
        // a second time (harmless — receivers are idempotent), but this one wins the race, so
        // the reason must ride here or the other tabs' banner depends on arrival order.
        postSessionEnd(reason);
    }
}

/**
 * The shared 401 refresh-and-retry handler (CT-36 hard requirement: ONE handler for both
 * clients, never two independent ones). Register BEFORE the legacy success/error interceptor
 * pair so a successful retry short-circuits the isUnauthorized→logout side effects.
 *
 * Skips (the original 401 falls through to the legacy interceptor):
 * - skipAuthRefresh requests — their 401 answers a question (e.g. a spent reset link), not
 *   "token expired"; refreshing would be wasted and retrying would loop.
 *   (silentError alone only suppresses toasts — silent data fetches still deserve the
 *   silent refresh-and-retry.)
 * - requests already retried once (_authRetried)
 * - logout in progress, or no session token to refresh
 */
export function attachAuthRefreshInterceptor(client: AxiosInstance): void {
    client.interceptors.response.use(undefined, async (error: AxiosError) => {
        const config = error?.config as (typeof error.config & { _authRetried?: boolean }) | undefined;
        const status = error?.response?.status;

        const { authToken, isLoggingOut } = authStore.getState();
        const shouldAttemptRefresh =
            status === 401 &&
            !!config &&
            !config._authRetried &&
            !config.skipAuthRefresh &&
            !isLoggingOut &&
            !!authToken;

        if (!shouldAttemptRefresh) {
            return Promise.reject(error);
        }

        let freshToken: string | null;
        try {
            freshToken = await refreshSession();
        } catch {
            // Transient refresh failure: reject the queued request without a logout storm.
            return Promise.reject(error);
        }

        if (!freshToken) {
            // Definitive deny (revoked family / dead cookie): the session cannot be recovered
            // from this tab. End it here rather than delegating to the legacy
            // 401 → isUnauthorized → /logout path — that path is suppressed for every
            // silentError request, which is every migrated /v1 screen, so the user was left
            // staring at an inline error whose Retry button could never succeed.
            endSession();
            return Promise.reject(error);
        }

        config._authRetried = true;
        // Drop the stale header so the request interceptor re-attaches the fresh token.
        if (config.headers && typeof (config.headers as { delete?: unknown }).delete === "function") {
            (config.headers as { delete: (name: string) => void }).delete("Authorization");
        }
        return client(config);
    });
}
